import React, { useEffect} from "react";
import { connect } from "react-redux";
import dayjs from "dayjs";
import { Form, DatePicker, Input, Select } from "antd";
import CrudButtons from "components/button/crudbuttons";
import FormItem from "components/fields/FormItem";
import { 
	getEmployeeDetails, doEmployeeCreate, doEmployeeUpdate 
} from "../../../../redux/employee/employeeState";
import { emailField, requiredField } from "utils/crudSagaHelper";
import { isSeparatedEmployee } from "utils";
import { DATE_FORMAT } from "../../../../variables/constants";
import { MODULE_EMPLOYEES } from "variables/constants";

const EmployeeInfoForm = ({ recordData, doCreate, doUpdate, onCancel, getRecord, recordId, submitLabel, ...props }) => {
  const [form] = Form.useForm();
  const { mode } = props;

  const employmentStatusId = Form.useWatch('emp_employment_status_id', form)

  useEffect(() => {
    if(recordId){
      getRecord(recordId);
    }
  }, [recordId, getRecord]);
  
  useEffect(() => {
    if (recordData) {
      form.resetFields();
      const { user_detail:userDetail, emp_hire_date, emp_birthdate, emp_separation_date, ...rest } = recordData;

      const initialValues = {
        ...rest,
        ...userDetail,
        ...userDetail?.address,
        ...userDetail?.contact_details,
        emp_hire_date: recordData?.emp_hire_date ? dayjs(recordData.emp_hire_date) : null,
        emp_birthdate: recordData?.emp_birthdate ? dayjs(recordData.emp_birthdate) : null,
        emp_separation_date: recordData?.emp_separation_date ? dayjs(recordData.emp_separation_date) : null,
      };
      
      form.setFieldsValue(initialValues);
    }
  }, [recordData, form]);


  const onSubmit = (formData) => {
    try {
      formData.emp_hire_date = formData.emp_hire_date?.format(DATE_FORMAT);
      formData.emp_birthdate = formData.emp_birthdate?.format(DATE_FORMAT);
      formData.emp_separation_date = formData.emp_separation_date
        ? formData.emp_separation_date.format(DATE_FORMAT)
        : null;
      formData.emp_is_exempt = formData.emp_is_exempt === false ? 0 : formData.emp_is_exempt;
      formData.emp_with_holiday_pay = formData.emp_with_holiday_pay === false ? 0 : formData.emp_with_holiday_pay;
      formData.emp_with_ot_pay = formData.emp_with_ot_pay === false ? 0 : formData.emp_with_ot_pay;
      formData.emp_with_nd_pay = formData.emp_with_nd_pay === false ? 0 : formData.emp_with_nd_pay;
      formData.emp_for_final_pay = formData.emp_for_final_pay === false ? 0 : formData.emp_for_final_pay;
      formData.emp_off_payroll = formData.emp_off_payroll === false ? 0 : formData.emp_off_payroll;
      formData.cde_type = "contact";

      recordData?.emp_id > 0 ? doUpdate(formData) : doCreate(formData);

    } catch (error) {
      console.error("Save failed:", error);
    }
  };

  return (
    <div className="w-full max-h-[80vh] overflow-auto mb-4">
    <Form className="form"
      layout="vertical" 
      form={form} 
      onFinish={onSubmit}
    >
      <FormItem 
        name="emp_id" 
        hidden
      >
				<Input type="hidden"/>
			</FormItem>
      <div className="mb-0">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-2 gap-x-4 px-2 w-full">
          <FormItem
            label="Employee ID"
            name="emp_code"
            rules={recordId > 0 ? [requiredField()] : []}
          >
            {recordId > 0 ? (
              <Input id="emp_code"
                className="input-field"
                maxLength={20}
              />
            ):(
              <label className="input-field py-2 px-3 border rounded bg-gray-100 text-gray-800">
                Auto-generated
              </label>
            )}
          </FormItem>
          
          <FormItem
            label="First Name"
            name="ude_first_name"
            rules={[requiredField()]}
          >
            <Input id="ude_first_name"
              className="input-field"
              maxLength={100}
            />
          </FormItem>

          <FormItem
            label="Last Name"
            name="ude_last_name"
            rules={[requiredField()]}
          >
            <Input id="ude_last_name"
              className="input-field"
              maxLength={100}
            />
          </FormItem>

          <FormItem
            label="Middle Initial"
            name="ude_middle_name"
          >
            <Input id="ude_middle_name"
              className="input-field"
              maxLength={100}
            />
          </FormItem>

          <FormItem
            label="Contact #"
            name="cde_mobile_number"
            rules={[
              {
                pattern: /^[0-9]+(-[0-9]+)*$/,
                message: "Contact number must contain digits only",
              },
            ]}
          >
            <Input type="text"
              id="cde_mobile_number"
              className="input-field"
              placeholder="09xx-xxx-xxxx"
              maxLength={15}
            />
          </FormItem>
          
          <FormItem
            label="Email"
            name="emp_email"
            rules={[requiredField(), emailField()]}
          >
            <Input type="text"
              className="input-field"
              id="emp_email"
              maxLength={100}
            />
          </FormItem>
        </div>
        <div className="px-2 w-full">
          <FormItem
            label="Address Line 1"
            name="add_address_line1"
            rules={[requiredField()]}
          >
            <Input type="text"
              className="input-field"
              id="add_address_line1"
              maxLength={100}
            />
          </FormItem>
        </div>
        <div className="px-2 w-full">
          <FormItem
            label="Address Line 2"
            name="add_address_line2"
          >
            <Input type="text"
              className="input-field"
              id="add_address_line2"
              maxLength={100}
            />
          </FormItem>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-2 gap-x-4 px-2 w-full">
          <FormItem
            label="City"
            name="add_city"
          >
            <Input type="text"
              className="input-field"
              id="add_city"
              maxLength={50}
            />
          </FormItem>

          <FormItem
            label="Postal Code"
            name="add_postal_code"
          >
            <Input type="text"
              className="input-field"
              id="add_postal_code"
              maxLength={50}
            />
          </FormItem>

          <FormItem
            label="Country"
            name="add_country_id"
            rules={[requiredField()]}
          >
            <Select id="add_country_id"
                className="input-field"
                showSearch
                allowClear={true}
                options={recordData?.countryList}
            />
          </FormItem>
        </div>
      </div>

      <div className="mb-0 mt-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-2 gap-x-4 px-2 w-full">
            <FormItem 
              label="Hire Date"
              name="emp_hire_date"
              rules={[
                requiredField(),
              ]}
            >
              <DatePicker id="emp_hire_date"
                format={DATE_FORMAT}
                className="input-field"
                style={{
                  width: "100%",
                }}
              />
            </FormItem>
            <FormItem 
              label="Employee Status"
              name="emp_employment_status_id"
              rules={[requiredField()]}
            >
              <Select id="emp_employment_status_id"
                className="input-field"
                showSearch
                allowClear={true}
                options={recordData?.employmentStatusList}
              />
            </FormItem>
            <FormItem 
              label="Employee Type" 
              name="emp_employment_type_id"
              rules={[requiredField()]}
            >
              <Select id="emp_employment_type_id"
                className="input-field"
                showSearch
                allowClear={true}
                options={recordData?.employmentTypeList}
              />
            </FormItem>
            <FormItem 
              label="Designation"
              name="emp_title"
              rules={[requiredField()]}
            >
              <Input type="text"
                id="emp_title"
                className="input-field"
                maxLength={100}
              />
            </FormItem>
            <FormItem 
              label="Department"
              name="emp_department_id"
              rules={[requiredField()]}
            >
              <Select id="emp_department_id"
                className="input-field"
                showSearch
                allowClear={true}
                options={recordData?.departmentList}
              />
            </FormItem>
            <FormItem 
              label="Job Level"
              name="emp_job_level_id"
              rules={[requiredField()]}
            >
              <Select
                id="emp_job_level_id"
                showSearch
                allowClear={true}
                options={recordData?.jobLevelList}
                className="input-field"
              />
            </FormItem>
            <FormItem 
              label="Client"
              name="emp_client_id"
              rules={[requiredField(),]}
            >
              <Select
                id="emp_client_id"
                showSearch
                allowClear={true}
                options={recordData?.clientList}
                className="input-field"
              />
            </FormItem>
            <FormItem 
              label="Team"
              name="emp_team_id"
            >
              <Select
                id="emp_team_id"
                showSearch
                allowClear={true}
                options={recordData?.teamList}
                className="input-field"
              />
            </FormItem>
            <FormItem 
              label="Birthdate"
              name="emp_birthdate"
            >
              <DatePicker id="emp_birthdate"
                className="input-field"
                format={DATE_FORMAT}
                style={{
                  width: "100%",
                }}
              />
            </FormItem>
            <FormItem 
              label="SSS #"
              name="emp_sss_number"
              rules={[
                {
                  pattern: /^[0-9]+(-[0-9]+)*$/,
                  message: "Only numbers are allowed",
                },
              ]}
            >
              <Input id="emp_sss_number"
                className="input-field"
                maxLength={20}
              />
            </FormItem>
            <FormItem 
              label="Pagibig #" 
              name="emp_pagibig_number"
              rules={[
                {
                  pattern: /^[0-9]+(-[0-9]+)*$/,
                  message: "Only numbers are allowed",
                },
              ]}
            >
              <Input id="emp_pagibig_number"
                className="input-field"
                maxLength={20}
              />
            </FormItem>
            <FormItem 
              label="Pagibig #(2)"
              name="emp_pagibig_number_2"
              rules={[
                {
                  pattern: /^[0-9]+(-[0-9]+)*$/,
                  message: "Only numbers are allowed",
                },
              ]}
            >
              <Input id="emp_pagibig_number_2"
                className="input-field"
                maxLength={20}
              />
            </FormItem>
            <FormItem 
              label="TIN #" 
              name="emp_tin"
              rules={[
                {
                  pattern: /^[0-9]+(-[0-9]+)*$/,
                  message: "Only numbers are allowed",
                },
              ]}
            >
              <Input id="emp_tin"
                className="input-field" 
                maxLength={20}
              />
            </FormItem>
            <FormItem 
              label="Bank Account Number"
              name="emp_bank_account" 
              rules={[
                {
                  pattern: /^[0-9]+(-[0-9]+)*$/,
                  message: "Only numbers are allowed",
                },
              ]}
            >
              <Input id="emp_bank_account"
                className="input-field"
                maxLength={20}
              />
            </FormItem>

            {isSeparatedEmployee(employmentStatusId) && 
              <FormItem 
                label="Separation Date"
                name="emp_separation_date"
                rules={[requiredField()]}
              >
                <DatePicker id="emp_separation_date"
                  format={DATE_FORMAT}
                  className="input-field"
                  style={{
                    width: "100%",
                  }}
                />
              </FormItem>
            }
            
          </div>
      </div>

      <div className="w-full flex justify-end">
        {(mode === "edit") && (
          <CrudButtons
            mode={mode}
            recordId={recordData?.emp_id ?? 0}
            isModal={true}
            modalId={recordData?.emp_id > 0 ? 'edit_employee' : 'add_employee'}
            cancelLabel="Cancel"
            doHideModal={onCancel}
            updateLabel="Update"
            submitLabel={submitLabel}
            moduleId={MODULE_EMPLOYEES}
          />
        )}
      </div>
    </Form>
    </div>
  );
};

const mapStateToProps = (state) => ({
  recordData: state.employee.employeeDetails?.data,
});

const mapDispatchToProps = (dispatch) => ({
  getRecord: (id) => dispatch(getEmployeeDetails(id)),
  doCreate: (data) => dispatch(doEmployeeCreate(data)),
  doUpdate: (data) => dispatch(doEmployeeUpdate(data)),
});

export default connect(mapStateToProps,mapDispatchToProps)(EmployeeInfoForm);
